// src/components/modals/SwaggerImportModal.tsx
import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Tabs,
  Tab,
  LinearProgress,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Paper,
  Divider
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  FolderOpen as FolderIcon,
  Link as LinkIcon,
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { API_BASE_URL } from '../../config/api';

interface SwaggerImportModalProps {
  open: boolean;
  onClose: () => void;
  containerId: string;
  containerName?: string;
  onImportComplete?: (result: any) => void;
}

interface ImportMethod {
  id: 'upload' | 'folder' | 'url';
  label: string;
  icon: React.ReactNode;
  description: string;
}

const importMethods: ImportMethod[] = [
  {
    id: 'upload',
    label: 'Upload Files',
    icon: <UploadIcon />,
    description: 'Upload Swagger files from your computer (Max 20 files)'
  },
  {
    id: 'folder',
    label: 'Scan Folder',
    icon: <FolderIcon />,
    description: 'Scan server folder for Swagger files (Bulk import 50+)'
  },
  {
    id: 'url',
    label: 'Import from URL',
    icon: <LinkIcon />,
    description: 'Import from API Gateway or external URL'
  }
];

export const SwaggerImportModal: React.FC<SwaggerImportModalProps> = ({
  open,
  onClose,
  containerId,
  containerName,
  onImportComplete
}) => {
  const [activeTab, setActiveTab] = useState<'upload' | 'folder' | 'url'>('upload');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [folderPath, setFolderPath] = useState('/app/data/swagger_imports/staging/');
  const [url, setUrl] = useState('');
  const [recursive, setRecursive] = useState(true);
  const [autoImport, setAutoImport] = useState(true);
  
  // Status tracking
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [importResult, setImportResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Folder scan results
  const [foundFiles, setFoundFiles] = useState<any[]>([]);
  const [showPreview, setShowPreview] = useState(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: 'upload' | 'folder' | 'url') => {
    setActiveTab(newValue);
    resetState();
  };

  const resetState = () => {
    setSelectedFiles([]);
    setFoundFiles([]);
    setShowPreview(false);
    setImportResult(null);
    setError(null);
    setProgress(0);
    setStatusMessage('');
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    
    // Validate file types
    const validFiles = files.filter(file => {
      const ext = file.name.toLowerCase();
      return ext.endsWith('.json') || ext.endsWith('.yaml') || ext.endsWith('.yml');
    });

    if (validFiles.length !== files.length) {
      setError('Some files were skipped. Only JSON and YAML files are supported.');
    }

    if (validFiles.length > 20) {
      setError('Maximum 20 files allowed for upload. Use folder scan for more files.');
      setSelectedFiles(validFiles.slice(0, 20));
    } else {
      setSelectedFiles(validFiles);
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError('Please select at least one file');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setProgress(0);

    try {
      const results = [];
      
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        setStatusMessage(`Uploading ${i + 1}/${selectedFiles.length}: ${file.name}`);
        setProgress(((i + 1) / selectedFiles.length) * 100);

        const formData = new FormData();
        formData.append('file', file);
        formData.append('container_id', containerId);

        const response = await fetch(`${API_BASE_URL}/document_intelligence/containers/${containerId}/upload-swagger`, {
          method: 'POST',
          body: formData
        });

        const result = await response.json();
        
        if (response.ok) {
          results.push({
            filename: file.name,
            status: 'success',
            api_name: result.api_info?.title,
            endpoints: result.summary?.total_endpoints
          });
        } else {
          results.push({
            filename: file.name,
            status: 'failed',
            error: result.detail || 'Upload failed'
          });
        }
      }

      setImportResult({
        total: selectedFiles.length,
        successful: results.filter(r => r.status === 'success').length,
        failed: results.filter(r => r.status === 'failed').length,
        results
      });

      setStatusMessage('Upload complete!');
      
      if (onImportComplete) {
        onImportComplete({ method: 'upload', results });
      }

    } catch (err: any) {
      setError(err.message || 'Upload failed');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFolderScan = async () => {
    if (!folderPath.trim()) {
      setError('Please enter a folder path');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setStatusMessage('Scanning folder...');

    try {
      const response = await fetch(`${API_BASE_URL}/document_intelligence/containers/${containerId}/scan-folder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          folder_path: folderPath,
          recursive,
          auto_import: autoImport
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || 'Folder scan failed');
      }

      if (autoImport) {
        setImportResult(result);
        setStatusMessage(`Import complete! ${result.imported} of ${result.total_found} files imported.`);
        
        if (onImportComplete) {
          onImportComplete({ method: 'folder_scan', result });
        }
      } else {
        setFoundFiles(result.found_files || []);
        setShowPreview(true);
        setStatusMessage(`Found ${result.total} Swagger files`);
      }

    } catch (err: any) {
      setError(err.message || 'Folder scan failed');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUrlImport = async () => {
    if (!url.trim()) {
      setError('Please enter a URL');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setStatusMessage('Importing from URL...');

    try {
      const response = await fetch(`${API_BASE_URL}/document_intelligence/containers/${containerId}/scan-folder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          container_id: containerId,
          url
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || 'URL import failed');
      }

      setImportResult(result);
      setStatusMessage('Import from URL complete!');
      
      if (onImportComplete) {
        onImportComplete({ method: 'url', result });
      }

    } catch (err: any) {
      setError(err.message || 'URL import failed');
    } finally {
      setIsProcessing(false);
    }
  };

  const renderUploadTab = () => (
    <Box sx={{ p: 2 }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          Upload up to 20 Swagger/OpenAPI files (JSON or YAML format).
          For more than 20 files, use the "Scan Folder" option.
        </Typography>
      </Alert>

      <Button
        variant="outlined"
        component="label"
        startIcon={<UploadIcon />}
        fullWidth
        sx={{ mb: 2, py: 2 }}
      >
        Select Swagger Files
        <input
          type="file"
          hidden
          multiple
          accept=".json,.yaml,.yml"
          onChange={handleFileSelect}
        />
      </Button>

      {selectedFiles.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, maxHeight: 300, overflow: 'auto' }}>
          <Typography variant="subtitle2" gutterBottom>
            Selected Files ({selectedFiles.length})
          </Typography>
          <List dense>
            {selectedFiles.map((file, index) => (
              <ListItem key={index}>
                <ListItemText
                  primary={file.name}
                  secondary={`${(file.size / 1024).toFixed(1)} KB`}
                />
                <IconButton
                  size="small"
                  onClick={() => setSelectedFiles(prev => prev.filter((_, i) => i !== index))}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      <Button
        variant="contained"
        onClick={handleUpload}
        disabled={selectedFiles.length === 0 || isProcessing}
        fullWidth
        startIcon={isProcessing ? <CircularProgress size={20} /> : <UploadIcon />}
      >
        {isProcessing ? 'Uploading...' : `Upload ${selectedFiles.length} File(s)`}
      </Button>
    </Box>
  );

  const renderFolderTab = () => (
    <Box sx={{ p: 2 }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          Scan a server folder for Swagger files. Best for bulk imports (50+ files).
        </Typography>
      </Alert>

      <TextField
        fullWidth
        label="Folder Path"
        value={folderPath}
        onChange={(e) => setFolderPath(e.target.value)}
        placeholder="/app/data/import_staging/"
        helperText="Enter the full path to the folder containing Swagger files"
        sx={{ mb: 2 }}
      />

      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={<Checkbox checked={recursive} onChange={(e) => setRecursive(e.target.checked)} />}
          label="Include subdirectories"
        />
        <FormControlLabel
          control={<Checkbox checked={autoImport} onChange={(e) => setAutoImport(e.target.checked)} />}
          label="Auto-import all found files"
        />
      </Box>

      {showPreview && foundFiles.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, maxHeight: 300, overflow: 'auto' }}>
          <Typography variant="subtitle2" gutterBottom>
            Found Files ({foundFiles.length})
          </Typography>
          <List dense>
            {foundFiles.map((file, index) => (
              <ListItem key={index}>
                <ListItemText
                  primary={file.filename}
                  secondary={`${(file.size / 1024).toFixed(1)} KB - ${file.relative_path}`}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      <Button
        variant="contained"
        onClick={handleFolderScan}
        disabled={!folderPath.trim() || isProcessing}
        fullWidth
        startIcon={isProcessing ? <CircularProgress size={20} /> : <FolderIcon />}
      >
        {isProcessing ? 'Scanning...' : autoImport ? 'Scan & Import' : 'Scan Folder'}
      </Button>
    </Box>
  );

  const renderUrlTab = () => (
    <Box sx={{ p: 2 }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          Import Swagger directly from a URL (API Gateway, GitHub, etc.)
        </Typography>
      </Alert>

      <TextField
        fullWidth
        label="Swagger URL"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://api.example.com/swagger.yaml"
        helperText="Enter the direct URL to the Swagger/OpenAPI specification"
        sx={{ mb: 2 }}
      />

      <Button
        variant="contained"
        onClick={handleUrlImport}
        disabled={!url.trim() || isProcessing}
        fullWidth
        startIcon={isProcessing ? <CircularProgress size={20} /> : <LinkIcon />}
      >
        {isProcessing ? 'Importing...' : 'Import from URL'}
      </Button>
    </Box>
  );

  const renderResults = () => {
    if (!importResult) return null;

    return (
      <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Import Results
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Chip
            icon={<CheckIcon />}
            label={`${importResult.successful || importResult.imported || 0} Succeeded`}
            color="success"
          />
          <Chip
            icon={<ErrorIcon />}
            label={`${importResult.failed || 0} Failed`}
            color="error"
          />
          <Chip
            icon={<InfoIcon />}
            label={`${importResult.total || importResult.total_found || 0} Total`}
            color="info"
          />
        </Box>

        {importResult.results && (
          <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
            {importResult.results.map((result: any, index: number) => (
              <ListItem key={index}>
                {result.status === 'success' ? (
                  <CheckIcon color="success" sx={{ mr: 1 }} />
                ) : (
                  <ErrorIcon color="error" sx={{ mr: 1 }} />
                )}
                <ListItemText
                  primary={result.filename || result.api_name}
                  secondary={result.error || `${result.endpoints} endpoints`}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { height: '80vh' } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">Import Swagger/OpenAPI</Typography>
            {containerName && (
              <Typography variant="body2" color="text.secondary">
                To: {containerName}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
          {importMethods.map((method) => (
            <Tab
              key={method.id}
              value={method.id}
              label={method.label}
              icon={method.icon}
              iconPosition="start"
            />
          ))}
        </Tabs>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {isProcessing && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {statusMessage}
            </Typography>
          </Box>
        )}

        {activeTab === 'upload' && renderUploadTab()}
        {activeTab === 'folder' && renderFolderTab()}
        {activeTab === 'url' && renderUrlTab()}

        {renderResults()}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          {importResult ? 'Done' : 'Cancel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SwaggerImportModal;